import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ROUTES } from '../../constants';
import BrandMark from '../common/BrandMark';
import './LegalPage.css';

const LAST_UPDATED = '3/23/2026';
const CONTACT_EMAIL = 'admin@polarace.gg';
const CONTACT_ADDRESS = '70 Post Road, Chicopee, Massachusetts, USA 01020';

// ── Terms of Service ──────────────────────────────────────────────────────────

const TOS_SECTIONS = [
  {
    heading: null,
    body: `Welcome to Polar Ace, operated by Space Ace Inc. ("Company", "we", "us", or "our"). These Terms of Service ("Terms") govern your access to and use of our website, mobile applications, software, analytics tools, streaming integrations, creator tools, and related services (collectively, the "Platform"). By accessing or using the Platform, you agree to these Terms. If you do not agree, you may not use the Platform.`,
  },
  {
    heading: '1. Eligibility',
    body: `You must be at least 18 years old to use the Platform. By using the Platform you represent that you have the legal capacity to enter into a binding agreement, your use of the Platform complies with applicable laws, and any information you provide is accurate. We may suspend or terminate accounts that violate these requirements.`,
  },
  {
    heading: '2. Platform Description',
    body: `The Platform provides services that may include creator accounts and profiles, creator-to-creator collaboration tools, live streaming and video integrations, advertising and brand partnership opportunities, analytics and performance tracking, creator monetization tools, and creator software integrations and extensions. We may modify, add, or discontinue features at any time.`,
  },
  {
    heading: '3. User Accounts',
    body: `To access certain features you must create an account. You agree to maintain the confidentiality of login credentials, accept responsibility for activity under your account, and notify us immediately of unauthorized access. We may suspend or terminate accounts involved in abuse, fraud, or violations of these Terms.`,
  },
  {
    heading: '4. User Content',
    body: `Users may upload, stream, publish, or otherwise submit content ("User Content"). You retain ownership of your content. By uploading content you grant the Company a worldwide, non-exclusive, royalty-free license to host, stream, display, reproduce, distribute, and promote such content for purposes of operating and promoting the Platform. You represent that you own the rights to the content, your content does not violate laws, and your content does not infringe third-party rights.`,
  },
  {
    heading: '5. Prohibited Content',
    body: `Users may not upload or stream content that includes illegal material, copyright infringement, harassment or threats, hate speech or discrimination, non-consensual explicit content, malware or malicious software, or fraudulent or misleading advertising. The Company may remove content at its discretion.`,
  },
  {
    heading: '6. Live Streaming Content & Moderation',
    body: `The Platform may enable users to broadcast live video, audio, or interactive content. Users are solely responsible for all content transmitted during live streams. The Company does not review all live streams in real time. By using live streaming features, you agree you will not stream unlawful or infringing content, you will comply with platform policies and applicable laws, and you are responsible for moderating your stream and viewer interactions. We may remove or interrupt live streams at our discretion. The Company shall not be liable for user-generated live content broadcast through the Platform.`,
  },
  {
    heading: '7. Advertising, Analytics & Automated Systems',
    body: `The Platform may include advertising systems, analytics tools, and automated placement technologies, including machine learning or artificial intelligence. These systems may be used to place advertisements alongside content, analyze engagement and performance metrics, provide insights to creators, and improve platform performance. By using the Platform, users agree that the Company may collect performance and engagement data, ads may be automatically inserted into platform content, and analytics may be used to improve advertising effectiveness. Advertising and revenue opportunities are not guaranteed.`,
  },
  {
    heading: '8. Third-Party Streaming Integrations',
    body: `The Platform may integrate with or display content from third-party streaming services. These integrations may include embedded video players, external stream feeds, third-party analytics, and creator streaming tools. The Company does not control third-party platforms and is not responsible for platform outages, API changes, policy changes, or content hosted externally. Use of those services remains subject to their respective terms.`,
  },
  {
    heading: '9. OBS Extensions & Creator Tools',
    body: `The Platform may provide downloadable extensions, plugins, or creator tools that interact with broadcasting software. These tools may allow overlay display, advertisement delivery, viewer engagement features, and analytics tracking. Users acknowledge that creator tools interact with third-party software environments, the Company does not control external broadcasting software, and performance issues may occur due to third-party software conflicts. Use of these tools is at the user's own risk.`,
  },
  {
    heading: '10. Platform Data & Metrics',
    body: `The Platform may provide metrics such as viewer counts, engagement data, advertisement impressions, and creator performance analytics. These metrics are informational estimates and may vary due to network conditions, third-party integrations, and measurement methodologies. The Company does not guarantee perfect accuracy of platform analytics.`,
  },
  {
    heading: '11. Creator Revenue & Fraud Protection',
    body: `Creators participating in monetization programs must comply with platform policies. Prohibited conduct includes artificially inflating views or engagement, using bots or automated traffic, manipulating advertising systems, and creating fraudulent accounts or interactions. The Company may withhold payments, reverse transactions, suspend monetization privileges, or terminate accounts if fraudulent activity is detected.`,
  },
  {
    heading: '12. Creator Collaboration & User Interactions',
    body: `The Platform may facilitate collaboration between users including creator partnerships, brand deals, and joint projects. The Company does not guarantee the conduct of other users. Users are solely responsible for agreements or transactions they enter into with other users. The Company is not responsible for contract disputes between creators, payments between users, or business relationships formed through the Platform.`,
  },
  {
    heading: '13. Algorithmic Recommendations & Platform Visibility',
    body: `The Platform may use automated systems to determine content recommendations, creator discovery, advertising placement, and search results and ranking. These systems may use data including engagement, viewer behavior, and platform activity. The Company does not guarantee visibility, promotion, or discovery for any user or content. Algorithmic systems may change at any time to improve platform functionality.`,
  },
  {
    heading: '14. Payments & Monetization',
    body: `Creators may earn revenue through advertising programs, brand collaborations, viewer payments, and other monetization tools. Payment terms may include minimum payout thresholds, processing delays, and payment processor requirements. Creators are responsible for paying applicable taxes.`,
  },
  {
    heading: '15. Copyright & DMCA',
    body: `If you believe content on the Platform infringes copyright, you may submit a DMCA notice to: Email: ${CONTACT_EMAIL} — Address: ${CONTACT_ADDRESS}. We may remove infringing content and terminate repeat infringers.`,
  },
  {
    heading: '16. Platform Security & Acceptable Use',
    body: `Users may not attempt to hack or reverse engineer the Platform, use automated bots without authorization, scrape platform data, or interfere with platform security systems. Violations may result in termination.`,
  },
  {
    heading: '17. Termination',
    body: `We may suspend or terminate accounts if users violate these Terms, engage in illegal activity, or create risk or liability for the Company. Users may stop using the Platform at any time.`,
  },
  {
    heading: '18. Disclaimer of Warranties',
    body: `The Platform is provided "AS IS" and "AS AVAILABLE." We do not guarantee that the Platform will always be available, services will be uninterrupted, or content will be accurate.`,
  },
  {
    heading: '19. Limitation of Liability',
    body: `To the fullest extent permitted by law, the Company is not liable for indirect damages, loss of profits or data, content posted by users, or third-party platform actions. Total liability shall not exceed amounts paid by the user in the previous 12 months.`,
  },
  {
    heading: '20. Indemnification',
    body: `You agree to indemnify and defend the Company against claims arising from your use of the Platform, your content, or your violation of these Terms.`,
  },
  {
    heading: '21. Governing Law',
    body: `These Terms are governed by the laws of the State of Delaware.`,
  },
  {
    heading: '22. Arbitration',
    body: `Disputes will be resolved through binding arbitration, except where prohibited by law.`,
  },
  {
    heading: '23. Changes to Terms',
    body: `We may update these Terms at any time. Continued use of the Platform constitutes acceptance of the updated Terms.`,
  },
  {
    heading: '24. Contact',
    body: `Company: Space Ace Inc. dba Polar Ace — Email: ${CONTACT_EMAIL} — Address: ${CONTACT_ADDRESS}`,
  },
];

// ── Privacy Policy ────────────────────────────────────────────────────────────

const PRIVACY_SECTIONS = [
  {
    heading: null,
    body: `This Privacy Policy explains how Space Ace Inc. dba Polar Ace ("Company", "we", "us", or "our") collects, uses, and protects information when you use Polar Ace (the "Platform"). By using the Platform, you consent to the practices described in this policy.`,
  },
  {
    heading: '1. Information We Collect',
    body: `We may collect the following types of information.\n\nAccount Information: When you create an account we may collect your name, email address, username, profile information, and password (stored securely).\n\nCreator & Platform Usage Information: We may collect information related to your activity on the Platform, including content you upload or stream, engagement metrics (views, likes, interactions), collaboration activity, and analytics and performance metrics.\n\nDevice and Technical Information: When you use the Platform we may collect your IP address, device type, operating system, browser type, network information, and crash and diagnostic data.\n\nPayment Information: If monetization features are used, payment processing may be handled by third-party processors (Stripe Inc.). We may collect payment account identifiers, transaction history, and payout information.`,
  },
  {
    heading: '2. How We Use Information',
    body: `We use information to provide and operate the Platform, enable creator collaboration features, provide analytics and insights, deliver advertising services, improve platform functionality, prevent fraud and abuse, maintain platform security, and communicate with users.`,
  },
  {
    heading: '3. Advertising & Analytics',
    body: `The Platform may use data to support advertising placement, performance measurement, audience analytics, and creator insights. Analytics may include engagement statistics and viewer behavior. Some analytics systems may use automated or machine-learning technologies.`,
  },
  {
    heading: '4. Cookies and Tracking Technologies',
    body: `We may use cookies or similar technologies to maintain login sessions, analyze usage patterns, improve performance, and personalize user experience. Users may adjust browser settings to disable cookies, though some features may not function properly.`,
  },
  {
    heading: '5. Sharing of Information',
    body: `We may share information with service providers that assist in operating the Platform including cloud hosting providers, payment processors, analytics services, and security and fraud prevention services. We may also disclose information when required to comply with legal obligations, respond to lawful requests, or protect rights or safety. If the Company is involved in a merger, acquisition, or sale of assets, user information may be transferred.`,
  },
  {
    heading: '6. Data Retention',
    body: `We retain information as long as necessary to provide services, maintain platform functionality, and comply with legal obligations. Users may request deletion of their account information where permitted by law.`,
  },
  {
    heading: '7. Data Security',
    body: `We use reasonable security measures to protect information, including encryption, access controls, and secure infrastructure. However, no system can guarantee absolute security.`,
  },
  {
    heading: '8. International Users',
    body: `If you access the Platform from outside the United States, your information may be transferred to and processed in the United States or other jurisdictions.`,
  },
  {
    heading: "9. Children's Privacy",
    body: `The Platform is not intended for users under the age of 18. We do not knowingly collect personal information from children.`,
  },
  {
    heading: '10. Your Privacy Rights',
    body: `Depending on your location, you may have rights including access to your data, request correction of information, and request deletion of data. Requests may be submitted to the contact address below.`,
  },
  {
    heading: '11. Changes to This Policy',
    body: `We may update this Privacy Policy periodically. The updated version will include a revised "Last Updated" date.`,
  },
  {
    heading: '12. Contact',
    body: `For privacy-related questions — Company: Space Ace Inc. dba Polar Ace — Email: ${CONTACT_EMAIL} — Address: ${CONTACT_ADDRESS}`,
  },
];

// ── Community Guidelines ──────────────────────────────────────────────────────

const GUIDELINES_SECTIONS = [
  {
    heading: null,
    body: `These Community Guidelines outline expected behavior on Polar Ace. Our goal is to create a safe, fun, and comfortable environment for creators, viewers, and their audience. We work hard to build and maintain a specific and friendly environment and culture in which we believe is the most entertaining for our community. We ask all of our users to acknowledge and respect the culture, make an effort to be friendly, treat people how you want to be treated, and make meaningful contributions to our community.`,
  },
  {
    heading: '1. Respectful Conduct',
    body: `Users must treat others respectfully. Prohibited behavior includes harassment or bullying, threats or intimidation, targeted harassment campaigns, and hate speech or discrimination.`,
  },
  {
    heading: '2. Illegal Content',
    body: `Content that violates laws is prohibited, including illegal activity, copyright infringement, fraud or scams, and distribution of illegal goods.`,
  },
  {
    heading: '3. Safety and Harmful Behavior',
    body: `Content that promotes or depicts harmful behavior is not allowed. Examples include encouraging violence, threats of physical harm, and dangerous activities likely to cause injury.`,
  },
  {
    heading: '4. Adult Content',
    body: `Explicit or pornographic content is strictly prohibited. Content involving minors in any sexual context is strictly prohibited. Violations may be reported to authorities.`,
  },
  {
    heading: '5. Intellectual Property',
    body: `Users must respect intellectual property rights. You may not upload or stream content that violates copyright, uses copyrighted material without permission, or misuses trademarks. Copyright holders may submit DMCA takedown requests.`,
  },
  {
    heading: '6. Platform Integrity',
    body: `Users may not manipulate the Platform through bots or automated accounts, fake engagement, artificially inflating views, or fraudulent advertising interactions. Accounts involved in such activity may be suspended.`,
  },
  {
    heading: '7. Spam and Deceptive Practices',
    body: `The following are prohibited: spam messages, misleading promotions, fake accounts, and phishing attempts.`,
  },
  {
    heading: '8. Creator Responsibility',
    body: `Creators are responsible for the content they produce and distribute. Creators should ensure content complies with laws, respect audience safety, and follow advertising disclosure requirements.`,
  },
  {
    heading: '9. Enforcement',
    body: `Violations may result in content removal, account suspension, monetization removal, or permanent bans. Serious violations may be reported to appropriate authorities.`,
  },
  {
    heading: '10. Reporting Violations',
    body: `Users may report violations through platform reporting tools or by contacting: Email: ${CONTACT_EMAIL}. Reports may be reviewed by moderation teams.`,
  },
  {
    heading: '11. Policy Updates',
    body: `We may update these guidelines to reflect changes in the Platform or legal requirements.`,
  },
];

// ── Page config ───────────────────────────────────────────────────────────────

const PAGES = {
  '/terms': {
    title: 'Terms of Service',
    sections: TOS_SECTIONS,
  },
  '/privacy': {
    title: 'Privacy Policy',
    sections: PRIVACY_SECTIONS,
  },
  '/guidelines': {
    title: 'Community Guidelines',
    sections: GUIDELINES_SECTIONS,
  },
};

// ── Component ─────────────────────────────────────────────────────────────────

function LegalPage() {
  const { pathname } = useLocation();
  const page = PAGES[pathname];

  if (!page) return null;

  return (
    <div className="legal-page">
      {/* Minimal nav */}
      <nav className="legal-nav">
        <Link to={ROUTES.AUTH} className="legal-nav__logo">
          <BrandMark className="legal-nav__brand-mark" compact />
        </Link>
        <div className="legal-nav__links">
          <Link to={ROUTES.TERMS} className={`legal-nav__link${pathname === ROUTES.TERMS ? ' legal-nav__link--active' : ''}`}>Terms</Link>
          <Link to={ROUTES.PRIVACY} className={`legal-nav__link${pathname === ROUTES.PRIVACY ? ' legal-nav__link--active' : ''}`}>Privacy</Link>
          <Link to={ROUTES.GUIDELINES} className={`legal-nav__link${pathname === ROUTES.GUIDELINES ? ' legal-nav__link--active' : ''}`}>Guidelines</Link>
        </div>
      </nav>

      {/* Content */}
      <main className="legal-page__main">
        <div className="legal-page__container">
          <h1 className="legal-page__title">{page.title}</h1>
          <p className="legal-page__updated">Last Updated: {LAST_UPDATED}</p>

          <div className="legal-page__body">
            {page.sections.map((section, i) => (
              <div key={i} className="legal-page__section">
                {section.heading && (
                  <h2 className="legal-page__section-heading">{section.heading}</h2>
                )}
                {section.body.split('\n\n').map((para, j) => (
                  <p key={j} className="legal-page__paragraph">{para}</p>
                ))}
              </div>
            ))}
          </div>

          <div className="legal-page__back">
            <Link to={ROUTES.AUTH} className="legal-page__back-btn">← Back to Sign In</Link>
          </div>
        </div>
      </main>
    </div>
  );
}

export default LegalPage;
